/* eslint-disable no-unused-vars */
import { FunctionComponent, useState, useEffect } from "react";
import LoginStyles from "./login.module.css";
import { Button } from "primereact/button";
import Logo from "assets/adroit_logo.png";
import { IUser } from "types/user.type";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { InputText } from "primereact/inputtext";
import { setToaster } from "common/toastStore";
import AuthService from "services/AuthService";
import { useNavigate, useParams } from "react-router-dom";
import Loader from "common/Loader";
import TokenService from "services/TokenService";

interface LoginProps {}

interface ResetForm extends IUser {
  confirmPassword: string;
  currentPassword: string;
  newPassword: string;
}

const ChangePassword: FunctionComponent<LoginProps> = () => {
  const { token } = useParams<{ token: string }>();
  const getUserIdentity = TokenService.geUserIdentity();
  const navigate = useNavigate();
  const [currentPasswordShown, setCurrentPasswordShown] = useState(false);
  const [passwordShown, setPasswordShown] = useState(false);
  const [passwordShownConfirm, setPasswordShownConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showSuccessScreen, setShowSuccessScreen] = useState(false);
  const [countdown, setCountdown] = useState(5);

  const toggleCurrentPassword = () =>
    setCurrentPasswordShown(!currentPasswordShown);
  const togglePassword = () => setPasswordShown(!passwordShown);
  const togglePasswordConfirm = () =>
    setPasswordShownConfirm(!passwordShownConfirm);

  /** ✅ Yup Validation Schema */
  const validationSchema = Yup.object().shape({
    currentPassword: Yup.string().required("Email is required"),

    newPassword: Yup.string()
      .required("New Password is required")
      .min(8, "Password must be at least 8 characters long")
      .matches(
        /[!@#$%^&*(),.?":{}|<>]/,
        "Must contain at least one special character"
      ),

    confirmPassword: Yup.string()
      .required("Confirm password is required")
      .oneOf([Yup.ref("newPassword"), null], "Passwords must match"),
  });

  const formOptions = {
    resolver: yupResolver(validationSchema),
    mode: "onBlur",
  };

  const { register, handleSubmit, formState } = useForm<ResetForm>(
    formOptions as object
  );
  const { errors, isValid } = formState;

  const onSubmit = (data: ResetForm) => {
    if (!isValid) return;
    setLoading(true);

    AuthService.updatePassword(
      data.currentPassword,
      data.newPassword,
      getUserIdentity
    )
      .then((_response: any) => {
        setLoading(false);
        setToaster({
          severity: "success",
          summary: "Success",
          detail: "Password updated successfully!",
        });
        TokenService.removeUser();
        setShowSuccessScreen(true);
        setCountdown(5);
      })
      .catch((error: { response: { data: any[] } }) => {
        setToaster({
          severity: "error",
          summary: "Error Message",
          detail: error?.response?.data?.[0] || "Something went wrong",
        });
        setLoading(false);
      });
  };

  // Countdown effect
  useEffect(() => {
    if (showSuccessScreen && countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (showSuccessScreen && countdown === 0) {
      navigate("/login");
    }
  }, [showSuccessScreen, countdown, navigate]);

  return (
    <>
      {showSuccessScreen ? (
        <div className="m-auto">
          <div className="mb-6 flex justify-center">
            <img src={Logo} alt="Adroit Logo" />
          </div>
          <div className="bg-white w-96 rounded-2xl p-6 shadow-lg m-auto text-center">
            <div className="mb-4">
              <i
                className="pi pi-check-circle text-green-500"
                style={{ fontSize: "4rem" }}
              ></i>
            </div>
            <h2 className="text-xl font-bold mb-4">
              Successfully Updated Password
            </h2>
            <p className="text-md mb-6">
              You are successfully logged out and will be redirected to login
              screen in{" "}
              <span className="font-bold text-xl text-blue-600">
                {countdown}
              </span>{" "}
              seconds.
            </p>
          </div>
        </div>
      ) : (
        <div className="m-auto">
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="mb-6 flex justify-center">
              <img src={Logo} alt="Adroit Logo" />
            </div>

            <div className="bg-white w-96 rounded-2xl p-6 shadow-lg m-auto">
              <p className="text-center text-md font-medium py-1">
                Change your password
              </p>

              {/* Current Password Field */}
              <div className="my-6 relative z-0 w-full ">
                <div className="p-float-label p-input-icon-right w-full">
                  <i
                    className={`pi cursor-pointer ${
                      currentPasswordShown ? "pi-eye" : "pi-eye-slash"
                    }`}
                    onClick={toggleCurrentPassword}
                  />
                  <InputText
                    id="currentPassword"
                    {...register("currentPassword")}
                    type={currentPasswordShown ? "text" : "password"}
                    className="w-full text-sm"
                  />
                  <label htmlFor="currentPassword">
                    Enter current Password
                  </label>
                </div>
                <div className="text-xs text-red-900">
                  {errors?.currentPassword?.message}
                </div>
              </div>

              {/* Password Field */}
              <div className="my-6 relative z-0 w-full">
                <div className="p-float-label p-input-icon-right w-full">
                  <i
                    className={`pi cursor-pointer ${
                      passwordShown ? "pi-eye" : "pi-eye-slash"
                    }`}
                    onClick={togglePassword}
                  />
                  <InputText
                    id="newPassword"
                    {...register("newPassword")}
                    type={passwordShown ? "text" : "password"}
                    className="w-full text-sm"
                  />
                  <label htmlFor="newPassword">Enter Password</label>
                </div>
                <div className="text-xs text-red-900">
                  {errors?.newPassword?.message}
                </div>
              </div>

              {/* Confirm Password Field */}
              <div className="my-6 relative z-0 w-full">
                <div className="p-float-label p-input-icon-right w-full">
                  <i
                    className={`pi cursor-pointer ${
                      passwordShownConfirm ? "pi-eye" : "pi-eye-slash"
                    }`}
                    onClick={togglePasswordConfirm}
                  />
                  <InputText
                    id="confirmPassword"
                    {...register("confirmPassword")}
                    type={passwordShownConfirm ? "text" : "password"}
                    className="w-full text-sm"
                  />
                  <label htmlFor="confirmPassword">Confirm Password</label>
                </div>
                <div className="text-xs text-red-900">
                  {errors?.confirmPassword?.message}
                </div>
              </div>

              <div>
                <Button
                  label="Submit"
                  type="submit"
                  className={LoginStyles.login_submit}
                />
              </div>
            </div>
          </form>
        </div>
      )}

      {loading && <Loader />}
    </>
  );
};

export default ChangePassword;
